"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { onCLS, onLCP, onFCP, onTTFB } from "web-vitals";
import { useReportWebVitals } from "next/web-vitals";
import {
  getCookie,
  getFallbackClaimNumber,
  handleClaimUpsert,
} from "@/app/utils/functions";

const getCurrentUrl = () => {
  if (typeof window !== "undefined") return window.location.href;
  return "";
};

const getCurrentId = () =>
  localStorage.getItem("currentId") ||
  getCookie("currentId") ||
  getFallbackClaimNumber() ||
  "";

// Load existing metrics from localStorage or start fresh
const loadMetricsStore = () => {
  try {
    const raw = localStorage.getItem("page_speed_insights");
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    console.error("Failed to load metrics from localStorage", e);
    return {};
  }
};

// Save merged metrics to localStorage
const saveMetricsStore = (store) => {
  try {
    localStorage.setItem("page_speed_insights", JSON.stringify(store));
  } catch (e) {
    console.error("Failed to save metrics to localStorage", e);
  }
};

const sendMetric = async (metric, url) => {
  const currentId = getCurrentId();
  const metricsStore = loadMetricsStore();

  if (!currentId || !metric?.name) return;

  // Store/merge metric for the current URL
  if (!metricsStore[url]) {
    metricsStore[url] = {};
  }

  metricsStore[url][metric.name] = {
    delta: metric.delta,
    value: metric.value,
    rating: metric.rating,
    navigationType: metric.navigationType,
    entries: metric.entries,
  };

  // Save updated metrics to localStorage
  saveMetricsStore(metricsStore);

  // Send the full object for that URL
  const payload = {
    page_speed_insights: metricsStore,
  };

  await handleClaimUpsert(payload);
};

export function WebVitals() {
  const pathname = usePathname();

  useReportWebVitals((metric) => {
    const url = getCurrentUrl();
    sendMetric(metric, url);
  });

  useEffect(() => {
    const url = getCurrentUrl();

    // Explicitly track all metrics
    onCLS((m) => sendMetric(m, url));
    onLCP((m) => sendMetric(m, url));
    onFCP((m) => sendMetric(m, url));
    onTTFB((m) => sendMetric(m, url));
  }, [pathname]);

  return null;
}
